import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import Loader from '@/components/Loader';
import ToolBar from '@/components/ToolBar';
import { NotFoundRedirectionWrapper } from '@/components/NotFoundRedirectWrapper';
import { displayError, handleError } from '@/lib/error';
import { convertDateToDurationTime, convertISOToTime } from '@/lib/utils';
import { useSingleDeliveryUpdate, createDeliveryUpdate, updateDeliveryUpdate, useDeliveryDetail } from '@/services/deliveryService';
import { DeliveryUpdateFormData, createDeliveryUpdateSchema, DeliveryUpdateValidationContext } from './deliveryUpdate.schema';
import { DeliveryUpdateForm } from './DeliveryUpdateForm';
import { hasFeature } from '@/services/featureService';
import { parseTime } from '@/utils/helper';
import type { DeliveryDetailResponse } from '@/types/deliveryDetail.types';
import type { AdditionalFee, DeliveryUpdateDetail } from '@/types/delivery.types';

type DeliveryUpdateMode = 'add' | 'edit' | 'view';

export interface DeliveryPanelNavigationState {
  openDeliveryId: string;
  clientName?: string | null;
}

const isDeliveryUpdateMode = (value: string | null): value is DeliveryUpdateMode =>
  value === 'add' || value === 'edit' || value === 'view';

interface DeliveryUpdateFormShellProps {
  mode: DeliveryUpdateMode;
  workOrderId: string;
  updateId?: string;
  validationContext: DeliveryUpdateValidationContext;
  workOrderData: DeliveryDetailResponse;
  updateData?: DeliveryUpdateDetail;
  isFetching: boolean;
  isError: boolean;
  canEditDeliveryUpdates: boolean;
  pageTitle: string;
}

const DeliveryUpdateFormShell: FC<DeliveryUpdateFormShellProps> = ({
  mode,
  workOrderId,
  updateId,
  validationContext,
  workOrderData,
  updateData,
  isFetching,
  isError,
  canEditDeliveryUpdates,
  pageTitle,
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isMediaUploading, setIsMediaUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const isReadOnly = mode === 'view';

  const deliveryUpdateSchema = useMemo(
    () => createDeliveryUpdateSchema(validationContext),
    [validationContext]
  );

  const form = useForm<DeliveryUpdateFormData>({
    resolver: isReadOnly ? undefined : zodResolver(deliveryUpdateSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      receivedBy: '',
      hasReturn: false,
      returnName: '',
      additionalFees: [],
      images: [],
    },
  });

  const { reset, handleSubmit, trigger, setValue, formState: { isValid } } = form;

  useEffect(() => {
    if (mode !== 'add' || !workOrderData) return;

    const woReturnDate = workOrderData.deliveryInformation?.returnDate;
    if (!woReturnDate) return;

    setValue('hasReturn', true, { shouldValidate: true });
    setValue('returnDate', new Date(woReturnDate), { shouldValidate: true });
  }, [mode, workOrderData, setValue]);

  useEffect(() => {
    if (updateData && (mode === 'edit' || mode === 'view')) {
      reset({
        completedDate: updateData.completedDate ? new Date(updateData.completedDate) : undefined,
        completedTime: parseTime(updateData.completedTime),
        receivedBy: updateData.receivedBy ?? '',
        date: updateData.date ? new Date(updateData.date) : undefined,
        waitingTime: parseTime(updateData.waitingTime),
        hasReturn: !!(updateData.returnDate ?? updateData.returnName),
        returnDate: updateData.returnDate ? new Date(updateData.returnDate) : undefined,
        returnName: updateData.returnName ?? '',
        additionalFees: updateData.additionalFees.map((fee) => ({
          feeId: fee.feeId,
          billingItemId: fee.billingItemId,
          feeType: fee.feeType as DeliveryUpdateFormData['additionalFees'][number]['feeType'],
          feeAmount: fee.feeAmount,
        })),
        images: updateData.media.map((mediaWrapper) => ({
          id: mediaWrapper.media.mediaId,
          name: mediaWrapper.media.path?.split('/').pop() ?? 'Image',
          mediaId: mediaWrapper.media.mediaId,
          updateMediaId: mediaWrapper.updateMediaId,
          mediaData: mediaWrapper.media,
        })),
      });
      if (mode === 'edit') {
        void trigger();
      }
    }
  }, [updateData, mode, reset, trigger]);

  const collectMediaIds = (images: DeliveryUpdateFormData['images']): string[] => {
    const list = images || [];
    const missingMediaId = list.some((img) => !img.mediaId);
    if (missingMediaId) {
      throw new Error('One or more images failed to upload. Remove them and try again.');
    }
    return list.map((img) => img.mediaId!);
  };

  const onSubmit = async (formData: DeliveryUpdateFormData) => {
    setIsSaving(true);

    try {
      const currentMediaIds = collectMediaIds(formData.images);

      const basePayload = {
        completedDate: formData.completedDate?.toISOString().split('T')[0],
        completedTime: convertISOToTime(formData.completedTime),
        receivedBy: formData.receivedBy.trim(),
        date: formData.date?.toISOString().split('T')[0],
        waitingTime: formData.waitingTime ? convertDateToDurationTime(formData.waitingTime) : undefined,
        returnDate: formData.hasReturn && formData.returnDate ? formData.returnDate.toISOString().split('T')[0] : undefined,
        returnName: formData.hasReturn && formData.returnName?.trim() ? formData.returnName.trim() : undefined,
      };

      let result;
      if (mode === 'add') {
        const payload = {
          ...basePayload,
          additionalFees: formData.additionalFees?.map((f) => ({
            feeType: f.feeType,
            feeAmount: f.feeAmount,
          })) || [],
          mediaIds: currentMediaIds,
        };
        result = await createDeliveryUpdate(workOrderId, payload);
      } else if (mode === 'edit' && updateId && updateData) {
        const existingFees = updateData.additionalFees || [];
        const currentFees = formData.additionalFees || [];
        const existingFeesMap = new Map(existingFees.map((f) => [f.feeId, f]));
        const feesToDelete: string[] = [];
        const feesToCreate: AdditionalFee[] = [];
        const feesToUpdate: { feeId: string; feeAmount: number }[] = [];
        const currentFeeIds = new Set(currentFees.map((f) => f.feeId).filter(Boolean));

        existingFees.forEach((fee) => {
          if (!currentFeeIds.has(fee.feeId)) {
            feesToDelete.push(fee.feeId);
          }
        });

        currentFees.forEach((currentFee) => {
          if (!currentFee.feeId) {
            feesToCreate.push({
              feeType: currentFee.feeType,
              feeAmount: currentFee.feeAmount,
            });
          } else {
            const originalFee = existingFeesMap.get(currentFee.feeId);
            if (originalFee && originalFee.feeAmount !== currentFee.feeAmount) {
              feesToUpdate.push({
                feeId: currentFee.feeId,
                feeAmount: currentFee.feeAmount,
              });
            }
          }
        });

        const existingMedia = updateData.media ?? [];
        const existingMediaIds = existingMedia.map((m) => m.media.mediaId);
        // PATCH mediaIds.delete expects join-table IDs (updateMediaId), not mediaId
        const mediaToDelete = existingMedia
          .filter((m) => !currentMediaIds.includes(m.media.mediaId))
          .map((m) => m.updateMediaId);
        const mediaToCreate = currentMediaIds.filter((id) => !existingMediaIds.includes(id));

        const payload = {
          ...basePayload,
          additionalFees: {
            create: feesToCreate,
            update: feesToUpdate,
            delete: feesToDelete,
          },
          mediaIds: {
            create: mediaToCreate,
            delete: mediaToDelete,
          },
        };
        result = await updateDeliveryUpdate(workOrderId, updateId, payload);
      } else {
        throw new Error(`Unsupported delivery update mode: ${mode}`);
      }

      if (result?.error) {
        displayError(result.error);
        return;
      }

      toast({
        description: mode === 'add' ? 'Delivery update created successfully' : 'Delivery update updated successfully',
      });

      await queryClient.invalidateQueries({ queryKey: ['delivery-update', workOrderId] });
      await queryClient.invalidateQueries({ queryKey: ['delivery-detail', workOrderId] });
      if (mode === 'edit' && updateId) {
        navigateToViewPage();
      } else {
        navigateToDeliveryList();
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const navigateToViewPage = () => {
    if (updateId) {
      navigate(`/delivery/work-order/${workOrderId}/delivery-update/${updateId}`);
    } else {
      navigateToDeliveryList();
    }
  };

  const handleBack = () => {
    if (mode === 'edit') {
      navigateToViewPage();
      return;
    }
    navigateToDeliveryList();
  };

  const navigateToDeliveryList = () => {
    const intakeId = workOrderData.commonDetails.intakeId;
    const clientName = workOrderData.summary.clientName;
    navigate(`/delivery?intakeId=${encodeURIComponent(intakeId)}`, {
      state: { openDeliveryId: intakeId, clientName } satisfies DeliveryPanelNavigationState,
    });
  };

  return (
    <FormProvider {...form}>
      <Loader isLoading={isFetching || isMediaUploading || isSaving}>
        <div className="flex flex-col w-full">
          <ToolBar
            title={pageTitle}
            additionalButton={
              mode === 'view' && canEditDeliveryUpdates && updateId ? (
                <Button
                  className="h-[36px] p-3 w-fit"
                  onClick={() =>
                    navigate(`/delivery/work-order/${workOrderId}/delivery-update/${updateId}?mode=edit`)
                  }
                  variant="default"
                >
                  Edit Delivery Updates
                </Button>
              ) : undefined
            }
          />

          {isError && mode !== 'add' ? (
            <div className="flex flex-col w-full items-center justify-center gap-4 p-6">
              <span className="text-placeholder">Failed to load delivery update</span>
              <Button type="button" variant="outline" onClick={handleBack}>
                Back
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              <form onSubmit={!isReadOnly ? handleSubmit(onSubmit) : (e) => e.preventDefault()} className="space-y-6">
                <DeliveryUpdateForm
                  isReadOnly={isReadOnly}
                  isAddMode={mode === 'add'}
                  workOrderData={workOrderData}
                  onMediaUploadingChange={setIsMediaUploading}
                />

                <div className="flex justify-start gap-4 pt-3">
                  <Button type="button" variant="outline" onClick={handleBack}>
                    {isReadOnly ? 'Back' : 'Cancel'}
                  </Button>
                  {!isReadOnly && (
                    <Button type="submit" disabled={isSaving || isMediaUploading || !isValid}>
                      Save
                    </Button>
                  )}
                </div>
              </form>
            </div>
          )}
        </div>
      </Loader>
    </FormProvider>
  );
};

export const DeliveryUpdateManage: FC = () => {
  const [searchParams] = useSearchParams();
  const modeParam = searchParams.get('mode');
  const { workOrderId, updateId } = useParams<{ workOrderId: string; updateId?: string }>();

  const mode: DeliveryUpdateMode = isDeliveryUpdateMode(modeParam)
    ? modeParam
    : updateId
      ? 'view'
      : 'add';

  const isDeliveryUpdatesEnabled = hasFeature('delivery.deliveryUpdates');
  const canRead = hasFeature('delivery.deliveryUpdates.read');

  const { data: workOrderData, isFetching: isLoadingWorkOrder } = useDeliveryDetail(workOrderId ?? null);
  const canEditDeliveryUpdates = workOrderData?.actionFlags?.canEditDeliveryUpdates ?? false;

  const validationContext: DeliveryUpdateValidationContext | undefined = workOrderData
    ? {
        workOrderCreatedDate: workOrderData.clientDetails?.createdAt
          ? new Date(workOrderData.clientDetails.createdAt)
          : undefined,
        pickupDate: workOrderData.pickupInformation?.pickupDateTime
          ? new Date(workOrderData.pickupInformation.pickupDateTime)
          : undefined,
        deliveryDate: workOrderData.deliveryInformation?.deliveryDateTime
          ? new Date(workOrderData.deliveryInformation.deliveryDateTime)
          : undefined,
      }
    : undefined;

  const { data: updateData, isFetching, isError } = useSingleDeliveryUpdate(
    mode === 'edit' || mode === 'view' ? workOrderId ?? null : null,
    mode === 'edit' || mode === 'view' ? updateId ?? null : null
  );

  if (!workOrderId) {
    return (
      <NotFoundRedirectionWrapper isItemNotFound={true} itemName="Delivery Update">
        <></>
      </NotFoundRedirectionWrapper>
    );
  }

  if (!isDeliveryUpdatesEnabled) {
    return (
      <NotFoundRedirectionWrapper isItemNotFound={true} itemName="Delivery Update">
        <></>
      </NotFoundRedirectionWrapper>
    );
  }

  if (mode === 'view' && !canRead) {
    return (
      <NotFoundRedirectionWrapper isItemNotFound={true} itemName="Delivery Update">
        <></>
      </NotFoundRedirectionWrapper>
    );
  }

  if (mode === 'add' && !isLoadingWorkOrder && workOrderData && !canEditDeliveryUpdates) {
    return (
      <NotFoundRedirectionWrapper isItemNotFound={true} itemName="Delivery Update">
        <></>
      </NotFoundRedirectionWrapper>
    );
  }

  if (mode === 'edit' && !isLoadingWorkOrder && workOrderData && !canEditDeliveryUpdates) {
    return (
      <NotFoundRedirectionWrapper isItemNotFound={true} itemName="Delivery Update">
        <></>
      </NotFoundRedirectionWrapper>
    );
  }

  if ((mode === 'edit' || mode === 'view') && !updateId) {
    return (
      <NotFoundRedirectionWrapper isItemNotFound={true} itemName="Delivery Update">
        <></>
      </NotFoundRedirectionWrapper>
    );
  }

  const workOrderNumber = workOrderData?.commonDetails?.workOrderNumber ?? '';
  const pageTitle = `W/O: ${workOrderNumber} - ${
    mode === 'add' ? 'Add Delivery Updates' : mode === 'edit' ? 'Edit Delivery Updates' : 'Delivery Updates'
  }`;

  return (
    <NotFoundRedirectionWrapper isItemNotFound={false} itemName="Delivery Update">
      <Loader isLoading={isLoadingWorkOrder}>
        {workOrderData && validationContext ? (
          <DeliveryUpdateFormShell
            mode={mode}
            workOrderId={workOrderId}
            updateId={updateId}
            validationContext={validationContext}
            workOrderData={workOrderData}
            updateData={updateData}
            isFetching={isFetching}
            isError={isError}
            canEditDeliveryUpdates={canEditDeliveryUpdates}
            pageTitle={pageTitle}
          />
        ) : null}
      </Loader>
    </NotFoundRedirectionWrapper>
  );
};
